import React, { useMemo, useState } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import { getTodayRange, getThisWeekRange, getThisMonthRange, formatDate } from '../utils/helpers';

export default function ChartsPage({ tasks, navigateTo }) {
  const [period, setPeriod] = useState('week'); // week, month, all

  const filteredTasks = useMemo(() => {
    if (period === 'all') return tasks;
    const [start, end] = period === 'week' ? getThisWeekRange() : getThisMonthRange();
    return tasks.filter(t => new Date(t.createdAt) >= start && new Date(t.createdAt) <= end);
  }, [tasks, period]);

  const completionData = useMemo(() => {
    const total = filteredTasks.length;
    const completed = filteredTasks.filter(t => t.status === 'completed').length;
    const rate = total ? Math.round((completed / total) * 100) : 0;
    return { total, completed, rate };
  }, [filteredTasks]);

  const statusData = useMemo(() => {
    const map = {};
    filteredTasks.forEach(t => { map[t.status] = (map[t.status] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [filteredTasks]);

  const typeData = useMemo(() => {
    const map = {};
    filteredTasks.forEach(t => { map[t.type] = (map[t.type] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredTasks]);

  const assigneeData = useMemo(() => {
    const map = {};
    filteredTasks.forEach(t => { map[t.assignee] = (map[t.assignee] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [filteredTasks]);

  const trendData = useMemo(() => {
    const days = period === 'week' ? 7 : 30;
    const now = new Date();
    const data = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const label = period === 'week' ? `${d.getMonth() + 1}/${d.getDate()}` : `${d.getMonth() + 1}/${d.getDate()}`;
      const created = tasks.filter(t => t.createdAt && t.createdAt.startsWith(dateStr)).length;
      const completed = tasks.filter(t => t.status === 'completed' && t.createdAt && t.createdAt.startsWith(dateStr)).length;
      data.push({ label, created, completed });
    }
    return data;
  }, [tasks, period]);

  const priorityData = useMemo(() => [
    { name: '紧急', value: filteredTasks.filter(t => t.priority === 'urgent').length, color: '#f43f5e' },
    { name: '高', value: filteredTasks.filter(t => t.priority === 'high').length, color: '#f97316' },
    { name: '中', value: filteredTasks.filter(t => t.priority === 'medium').length, color: '#3b82f6' },
    { name: '低', value: filteredTasks.filter(t => t.priority === 'low').length, color: '#9ca3af' },
  ], [filteredTasks]);

  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#f43f5e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-800">统计图表</h1>
        <div className="flex items-center space-x-2">
          {['week', 'month', 'all'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${period === p ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
              {p === 'week' ? '本周' : p === 'month' ? '本月' : '全部'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-sm text-slate-500">任务总数</div>
          <div className="text-3xl font-bold text-slate-800 mt-2">{completionData.total}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-sm text-slate-500">已完成</div>
          <div className="text-3xl font-bold text-emerald-600 mt-2">{completionData.completed}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-sm text-slate-500">完成率</div>
          <div className="text-3xl font-bold text-indigo-600 mt-2">{completionData.rate}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">任务状态分布</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">优先级分布</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={priorityData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} label>
                {priorityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">任务趋势（新建 vs 完成）</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="created" name="新建" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} />
            <Area type="monotone" dataKey="completed" name="完成" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">任务类型TOP10</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={typeData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} style={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">负责人任务分布</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={assigneeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
